<template>
  <v-card class="white border-boxGlobal">
    <v-card-title class="mb-5">Reporte de Costos de Traspaso </v-card-title>
    <v-card-text >
      <v-row class="mx-1 mb-5">
        <div class="row">
          <!-- Start date -->
          <v-col cols="12" sm="6" md="2"  lg="2" xl="2" class="mb-3 mb-md-0">
            <DatePicker 
              label="Fecha inicio: " 
              @dateSelected="(param) => (startDate = param)" 
              v-model="startDate"
              :value="startDate"
            >
            </DatePicker>
          </v-col>
          <!-- End date -->
          <v-col cols="12" sm="6" md="2" lg="2" xl="2" class="mb-3 mb-md-0">
            <DatePicker 
              label="Fecha fin: " 
              @dateSelected="(param) => (endDate = param)" 
              v-model="endDate"
              :value="endDate">
            </DatePicker>
          </v-col>
          <!-- Origin warehouse -->
          <v-col cols="12" sm="6" md="3" lg="3" xl="3">
            <v-autocomplete 
              attach 
              :items="originWarehouses"
              label="Almacén (origen): " 
              v-model="originWarehouse" 
              item-text="Warehouse"
              item-value="ID_Warehouse" 
              outlined 
              dense
            >
            </v-autocomplete>
          </v-col>
          <!-- Destiny warehouse -->
          <v-col cols="12" sm="6" md="3" lg="3" xl="3">
            <v-autocomplete 
              attach 
              :items="destinyWarehouses"
              label="Almacén (destino): " 
              v-model="destinyWarehouse" 
              item-text="Warehouse"
              item-value="ID_Warehouse" 
              outlined 
              dense
              :rules="[(value) => !!value || 'Este campo es requerido']"
            >
            </v-autocomplete>
          </v-col>
          <!-- Search button -->
          <v-col cols="12" md="2" lg="2" xl="2" :align="'center'">
            <div>
              <Button block @click="getReport" :disabled="!destinyWarehouse">
                <div class="d-flex align-center">
                  <v-icon class="mr-1">mdi-magnify</v-icon>
                  <div>Buscar</div>
                </div>
              </Button>
            </div>
          </v-col>
        </div >
      </v-row>
      <v-row class="mx-1">
        <div>
          <Button block @click="exportPdf" :disabled="!report || !report.length">
              <div class="d-flex align-center">
                <v-icon class="mr-1">mdi-file-pdf-box</v-icon>
                <div>Exportar PDF</div>
              </div>
            </Button>
        </div>
      </v-row >
      <v-row class="row overflow-x-auto">
        <!-- Table -->
         <DxDataGrid 
            ref="dxDataGridToPrint" 
            :data-source="report" 
            :show-borders="true"
            :scrolling="{ useNative: true }" 
            :column-auto-width="true" 
            :word-wrap-enabled="true"
            @exporting="onExporting"
          >
            <DxExport :enabled="true" />

            <DxColumn data-field="transferencia" caption="ID transfencia" />
            <DxColumn data-field="fecha_transferencia" caption="Fecha de transferencia" />
            <DxColumn data-field="ID_Item" caption="ID Producto" />
            <DxColumn data-field="codigo_alterno" caption="Código alterno" />
            <DxColumn data-field="producto" caption="Producto" />
            
            <DxColumn data-field="OriginID_Warehouse" caption="Almacén de origen" cell-template="cell-origin" />
            <template #cell-origin="{ data }">
              {{ getWarehouseById(data.data.OriginID_Warehouse).Warehouse }}
            </template>

            <DxColumn data-field="DestinyID_Warehouse" caption="Almacén de destino" cell-template="cell-destiny" />
            <template #cell-destiny="{ data }">
              {{ getWarehouseById(data.data.DestinyID_Warehouse).Warehouse }}
            </template>

            <DxColumn data-field="cantidad" caption="cantidad" />
            <DxColumn data-field="costo_unitario" :caption="`Costo unitario (${currency})`" />
            <DxColumn data-field="costo_total" :caption="`Costo total (${currency})`" />

            <DxPager :showPageSizeSelector="true" :allowedPageSizes="[10, 30, 50]" />
            <DxPaging :page-size="10" />
        </DxDataGrid>
      </v-row>
    </v-card-text>
    <Block v-if="loading"></Block>

    <alert
      v-model="alert.show"
      :type="alert.type"
      :title="alert.title"
      :message="alert.message"
    >
    </alert>
  </v-card>
</template>

<script>
import Block from "@/components/Block";
import { formatDate } from "@/helpers/formatDate";
import DatePicker from "@/components/DatePicker.vue"
import Button from '@/components/Button.vue'
import {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxExport,
    DxPager,
} from "devextreme-vue/data-grid";
import { printToExcel } from "@/helpers/printToexcel";

export default {
  name: 'TransferCostsReport',
  components: {
    Block,
    DatePicker,
    Button,
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxExport,
    DxPager,
  },
  data () {
    return {
      loading: false,
      business: '',
      branch: '',
      // Dates
      startDate: formatDate(new Date(
        Date.now() - new Date().getTimezoneOffset() * 60000
      )
        .toISOString()
        .substring(0, 10)),
      endDate: formatDate(new Date(
        Date.now() - new Date().getTimezoneOffset() * 60000
      )
        .toISOString()
        .substring(0, 10)),
      // Warehouses
      warehouses: [],
      originWarehouse: 'all',
      destinyWarehouse: '',
      report: [],
      currency: JSON.parse(localStorage.getItem("branch")).Currency || '',
      alert: {
        type: "success",
        show: false,
        header: "",
        body: "",
      },
    }
  },
  computed: {
    originWarehouses () {
      const filteredWarehouses = this.warehouses.filter(
        (warehouse) => warehouse.ID_Warehouse != this.destinyWarehouse
      );

      const options = [
        {
          Warehouse: 'Todos',
          ID_Warehouse: 'all'
        },
        ...filteredWarehouses
      ];

      return options;
    },
    destinyWarehouses () {
      const filteredWarehouses = this.warehouses.filter(
        (warehouse) => warehouse.ID_Warehouse != this.originWarehouse
      );

      const options = [
        ...filteredWarehouses
      ];

      return options;
    }
  },
  methods: {
    async getWarehouses() {
      let branch = JSON.parse(localStorage.getItem("user")).businessid || "";
      let business = JSON.parse(localStorage.getItem("user")).branch || "";
      
      try {
        const response = await this.$API.warehouse.getWarehouses({
          branch,
          business,
          notShowReserve: 1, 
        });

        this.warehouses = response;
      } catch (err) {
        console.log(err);
      }
    },
    getWarehouseById(id) {
      if (id === 'all') {
        return {
          Warehouse: 'Todos'
        }
      }

      const found = this.warehouses.find((wh) => {
        return wh.ID_Warehouse === id
      })

      return found ? found : {
        Warehouse: 'Warehouse not found'
      }
    },
    async getReport() {
      this.loading = true
      try {
        const params = {
          startDate: this.startDate,
          endDate: this.endDate,
          originWarehouse: this.originWarehouse,
          destinyWarehouse: this.destinyWarehouse,
        }

        const response = await this.$API.inventoryReports.getTransferCostsReport(params)

        this.report = response
      } catch(err) {
        console.log(err)
      }
      this.loading = false
    },
    async exportPdf() {
      this.loading = true;
      try {
        // Obtener datos de sucursal y negocio
        let branch = JSON.parse(localStorage.getItem("user")).branch;
        let business = JSON.parse(localStorage.getItem("user")).businessid;
        let [businessInfo] = await this.$API.branches.findSpecificBranch({
          branch,
          business,
        });

        let logoUrl = businessInfo.BusinessLogoPath;
        let logoImg = null;
        if (logoUrl) {
          try {
            const { imageBase64 } = await this.$API.utilities.getImageInBase64({
              file: logoUrl,
            });
            logoImg = imageBase64;
          } catch (e) {
            logoImg = null;
            console.warn("No se pudo cargar el logo en base64:", e.message);
          }
        }

        // Crear PDF
        const jsPDF =
          (await import("jspdf")).jsPDF || (await import("jspdf")).default;
        const autoTable = (await import("jspdf-autotable")).default;
        const doc = new jsPDF("p", "mm", "a4");
        const pageWidth = doc.internal.pageSize.getWidth();
        let y = 12;

        // Logo y datos empresa
        if (logoImg) {
          doc.addImage(logoImg, "PNG", 10, 8, 38, 18);
        }
        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text(businessInfo?.BusinessName || "", 12, 30);
        doc.setFont(undefined, "normal");
        let yInfo = 35;
        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text(
          "Fecha de impresión: " + new Date().toLocaleDateString(),
          pageWidth - 12,
          15,
          { align: "right" }
        );

        doc.setLineWidth(0.3);
        doc.line(10, yInfo + 2, pageWidth - 10, yInfo + 2);
        y = yInfo + 8;

        // Encabezado de transferencia
        doc.setFontSize(13);
        doc.setFont(undefined, "bold");
        doc.text("Costos de traspaso", pageWidth / 2, y, {
          align: "center",
        });
        y += 8;
        doc.setFontSize(10);
        doc.setFont(undefined, "normal"); // Construir array de encabezado con fechas dinámicas
        const encabezado = [
          `Fecha inicio: ${this.startDate}`,
          `Fecha fin: ${this.endDate}`,
          `Origen: ${this.getWarehouseById(this.originWarehouse).Warehouse}`,
          `Destino: ${this.getWarehouseById(this.destinyWarehouse).Warehouse}`,
        ];

        // Agregar encabezado básico
        encabezado.forEach((line) => {
          doc.text(line, 12, y);
          y += 6;
        });

        y += 2;
        // Tabla de productos
        const tableData = this.report || [];
        const tableColumns = [
          { header: "Fecha transferencia", dataKey: "fecha_transferencia" },
          { header: "ID Producto", dataKey: "ID_Item" },
          { header: "Código alterno", dataKey: "codigo_alterno" },
          { header: "Producto", dataKey: "producto" },
          { header: "Cantidad Solicitada", dataKey: "cantidad" },
          { header: `Costo Unitario (${this.currency})`, dataKey: "costo_unitario" },
          { header: `Costo Total (${this.currency})`, dataKey: "costo_total" },
        ];

        const rows = tableData.map((item) => ({
          fecha_transferencia: formatDate(item.fecha_transferencia),
          ID_Item: item.ID_Item,
          codigo_alterno: item.codigo_alterno || "",
          producto: item.producto,
          cantidad: item.cantidad,
          costo_unitario: item.costo_unitario,
          costo_total: item.costo_total,
        }));

        const totalUnitCost = tableData.reduce((sum, item) => sum + Number(item.costo_unitario || 0), 0);
        const totalCost = tableData.reduce((sum, item) => sum + Number(item.costo_total || 0), 0);

        const totalsRow = {
          fecha_transferencia: "",            // Empty or label as needed
          ID_Item: "",                       // Empty cells
          codigo_alterno: "",
          producto: "",               // Label in producto column, for example
          cantidad: "Totales:",                      // Empty or sum quantity if you want
          costo_unitario: totalUnitCost.toFixed(2),  // Your total costo_unitario
          costo_total: totalCost.toFixed(2),          // Your total costo_total
        };

        const rowsWithTotals = [...rows, totalsRow];

        autoTable(doc, {
          startY: y,
          head: [tableColumns.map((col) => col.header)],
          body: rowsWithTotals.map((row) => tableColumns.map((col) => row[col.dataKey])),
          styles: { fontSize: 8, cellPadding: 1.5 },
          headStyles: {
            fillColor: [128, 128, 128],
            textColor: 255,
            halign: "center",
            valign: "middle",
          },
          bodyStyles: { valign: "top" },
          tableWidth: "auto",
          columnStyles: {
            0: { cellWidth: "auto", halign: "center", valign: 'middle' },
            1: { cellWidth: "auto", halign: "center", valign: 'middle' },
            2: { cellWidth: "auto", halign: "center", valign: 'middle' },
            3: { cellWidth: "auto", halign: "center", valign: 'middle' },
            4: { cellWidth: "auto", halign: "center", valign: 'middle' },
            5: { cellWidth: "auto", halign: "center", valign: 'middle' },
            6: { cellWidth: "auto", halign: "center", valign: 'middle' },
          },
          theme: "grid",
          foot: [
            [
              { content: "", styles: { halign: "center" } },
              { content: "", styles: { halign: "center" } },
            ],
          ],
          footStyles: {
            fillColor: [255, 255, 255],
            textColor: 20,
            fontStyle: "bold",
            fontSize: 10,
          },
          didParseCell: (data) => {
            const lastRowIndex = rowsWithTotals.length - 1;
            const lastColIndex = tableColumns.length - 1;

            if (data.row.index === lastRowIndex) {
              // This is the totals row - customize style
              data.cell.styles.fontStyle = "bold";
              data.cell.styles.fillColor = 245;
              
              if (data.column.index < lastColIndex - 1) {
                data.cell.styles.lineWidth = 0; 
              } else {
                data.cell.styles.lineWidth = 0.3;
                data.cell.styles.lineColor = 200;
              }

              if (data.column.dataKey === "producto") {
                data.cell.styles.halign = "right"; // align "Totales:" right if desired
              } else if (
                data.column.dataKey === "costo_unitario" ||
                data.column.dataKey === "costo_total"
              ) {
                data.cell.styles.halign = "center";
              }
            }
          },
        });
        // Firmas
        doc.setFontSize(10);

        // Paginación
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.text(
            `Página ${i} de ${pageCount}`,
            pageWidth - 20,
            doc.internal.pageSize.getHeight() - 8,
            { align: "right" }
          );
        }
        window.open(doc.output("bloburl"), "_blank");
      } catch (error) {
        console.log(error)
        this.showError("Error", "Ha ocurrido un error generando el PDF.");
      } finally {
        this.loading = false;
      }
    },
    onExporting(e) {
      const columnsToSum = [
        `Costo unitario (${this.currency})`,
        `Costo total (${this.currency})`,
      ];

      printToExcel(e, "Reporte de Costos de Traslado", columnsToSum);
    },
    showAlert(title, message, type = "success") {
      this.alert.type = type;
      this.alert.title = title;
      this.alert.message = message;
      this.alert.show = true;
    },
    showError(title, message) {
      this.showAlert(title, message, "error");
    },
    closeAlert() {
      this.alert.show = false;
    },
  },
  async mounted() {
    this.loading = true;

    await this.getWarehouses();

    this.loading = false;
  }
}
</script>